import React, { useState, useEffect } from "react";
import { Button, CardHeader, Input, Modal, Table } from "reactstrap";
import { useNavigate } from "react-router-dom";
import {
  getJournalApi,
  publisherJoinPaperApi,
  researchpaperSearchApi,
} from "src/network/api";
import Pagination from "./Pagination";
import PageLayout from "../PageLayout";
import CountrySelector from "./CountrySelector";
import { toast } from "react-toastify";

const SearchResearchPaper = () => {
  const navigate = useNavigate();
  const [filters, setFilters] = useState({
    country: "",
    department: "",
  });
  const [searchQuery, setSearchQuery] = useState("");
  const [journals, setJournals] = useState([]); // State to hold journals
  const [countryId, setCountryId] = useState(0);
  const [stateId, setStateId] = useState(0);
  const [researchPaper, setResearchPaper] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const limit = 10;

  const [showModal, setShowModal] = useState(false);
  const [targetResearchPaper, setTargetResearchPaper] = useState(0);
  const [targetJournal, setTargetJournal] = useState(0);
  const handleFilterChange = (e: { target: { name: any; value: any } }) => {
    const { name, value } = e.target;
    setFilters({ ...filters, [name]: value });
  };
  const fetchResearchPaper = async (currentPage: number) => {
    const offset = currentPage;
    setLoading(true);
    setError("");
    try {
      const response = await researchpaperSearchApi({
        limit,
        offset,
        country: countryId || "",
        department: filters.department || "",
        name: searchQuery,
      });
      setResearchPaper(response.data.data);
      setTotalPages(response.data.totalPages);
    } catch (err) {
      setError("Failed to fetch reviewers. Please try again later.");
      console.error("Error fetching reviewers:", err);
      setResearchPaper([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchJournals = async () => {
    try {
      const response = await getJournalApi();
      setJournals(
        response.data?.filter((item) => {
          return item.approval_status == "approved";
        })
      );
    } catch (err) {
      console.error("Error fetching journals:", err);
    }
  };

  const handleModal = () => {
    setShowModal(false);
    setTargetJournal(0);
  };

  useEffect(() => {
    fetchResearchPaper(page);
  }, [filters, page, searchQuery, countryId]);

  useEffect(() => {
    fetchJournals();
  }, []);

  return (
    <PageLayout>
      <div className="container-fluid reviewer-table">
        <div className="reviewers-container-fluid">
          <div className="row align-items-center">
            {/* Search Bar */}
            <div className="col-md-4 mt-2">
              <Input
                type="text"
                className="search-input"
                placeholder="Search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>

            {/* Country Filter */}
            <div className="col-md-4">
              <CountrySelector
                currentCountry={countryId}
                onSelectCountryCallback={(selectedCountryId) => {
                  setCountryId(selectedCountryId);
                  setStateId(0);
                }}
              />
            </div>

            {/* Department Filter */}
            <div className="col-md-4">
              <Input
                type="select"
                name="department"
                id="department"
                className="filter-input"
                value={filters.department}
                onChange={handleFilterChange}
              >
                <option value="">All Departments</option>
                <option value="Literature">Literature</option>
                <option value="AI">AI</option>
                <option value="Quantum Mechanics">Quantum Mechanics</option>
              </Input>
            </div>
          </div>
        </div>

        {/* Table Section */}
        <div className="table-container">
          <CardHeader className="bg-primary">
            <h3 className="mb-0 text-center font-weight-bold text-white">
              Research Paper List
            </h3>
          </CardHeader>

          {loading ? (
            <p className="text-center">Loading research papers...</p>
          ) : error ? (
            <p className="text-danger">{error}</p>
          ) : researchPaper.length > 0 ? (
            <div className="table">
              <Table bordered hover responsive className="mb-0 " size="sm">
                <thead className="thead-light">
                  <tr>
                    <th>Paper Title</th>
                    <th>Subject(s)</th>
                    <th>Country</th>
                    {/* <th>Department</th> */}
                    <th>Keywords</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {researchPaper.map((researchpaper, index) => (
                    <tr>
                      <td>{researchpaper?.paper_title}</td>
                      <td>{researchpaper?.subjects}</td>
                      <td>{researchpaper?.country_name}</td>
                      {/* <td>{researchpaper?.department}</td> */}
                      <td>{researchpaper?.keywords}</td>
                      <td>
                        <button
                          className="btn btn-sm btn-primary"
                          onClick={() => {
                            setShowModal(true);
                            setTargetResearchPaper(researchpaper.paper_id);
                          }}
                        >
                          Ask to Publish
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          ) : (
            <p
              style={{
                marginBottom: "80px",
                marginTop: "80px",
                textAlign: "center",
                fontSize: "18px",
                color: "#6c757d",
              }}
            >
              No research paper found.
            </p>
          )}
          {researchPaper.length > 0 && (
            <div className="pagination-container">
              <Pagination total={totalPages} page={page} setPage={setPage} />
            </div>
          )}
        </div>
      </div>
      <Modal
        className="modal-dialog-centered"
        isOpen={showModal}
        toggle={() => setShowModal(!showModal)}
      >
        <div className="modal-header">
          <h6 className="modal-title" id="modal-title-default">
            Ask Author to Publish Paper In Your Journal
          </h6>
          <button
            aria-label="Close"
            className="close"
            data-dismiss="modal"
            type="button"
            onClick={() => setShowModal(false)}
          >
            <span aria-hidden={true}>×</span>
          </button>
        </div>
        <div className="modal-body">
          <div className="form-group">
            <label>Choose a Journal</label>
            <Input
              type="select"
              onChange={(e) => setTargetJournal(parseInt(e.target.value))}
            >
              <option value={0}>Select Journal</option>

              {journals.map((journal) => (
                <option key={journal.journal_id} value={journal.journal_id}>
                  {journal.journal_name}
                </option>
              ))}
            </Input>
          </div>
        </div>
        <div className="modal-footer">
          <Button
            color="primary"
            type="button"
            disabled={targetJournal === 0 || targetResearchPaper === 0}
            onClick={async () => {
              try {
                const response = await publisherJoinPaperApi(
                  targetJournal,
                  targetResearchPaper
                );

                if (response.status === 200 && response.data.status === 200) {
                  toast.success(response.data.message);
                } else {
                  toast.error(response.data.message);
                }
              } catch (err) {
                console.error("Error sending request:", err);
                toast.error(err.response?.data?.message ?? err.message);
              } finally {
                setShowModal(false);
              }
            }}
          >
            Send Request
          </Button>

          <Button
            className="ml-auto"
            color="link"
            data-dismiss="modal"
            type="button"
            onClick={handleModal}
          >
            Close
          </Button>
        </div>
      </Modal>
    </PageLayout>
  );
};

export default SearchResearchPaper;
